import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Button, Dropdown, Form, Input, Menu, message, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import axios from '../../api';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const UserGroupMemberManage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchUser, setSearchUser] = useState('');
  const searchInput = useRef(null);

  // 获取用户组信息
  const fetchGroupInfo = async () => {
    try {
      const response = await axios.get(`/api/users/groups/${groupId}/`);
      setGroupInfo(response.data);
    } catch (error) {
      message.error('获取用户组信息失败');
    }
  };

  // 获取成员列表
  const fetchMembers = async (page = 1, pageSize = 10, userKeyword = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/users/groups/${groupId}/members/`, {
        params: { page, page_size: pageSize, search: userKeyword }
      });
      setMembers(response.data.results || []);
      setPagination({ current: page, pageSize, total: response.data.count || 0 });
    } catch (error) {
      message.error('获取成员列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取所有用户（用于添加成员）
  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users/', { params: { page_size: 1000 } });
      let userList = [];
      if (response.data && Array.isArray(response.data)) {
        userList = response.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        userList = response.data.results;
      }
      setUsers(userList);
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  // 添加成员
  const handleAddMember = async (values) => {
    try {
      await axios.post(`/api/users/groups/${groupId}/members/`, {
        group_id: parseInt(groupId),
        user_id: values.user_id,
      });
      message.success('添加成员成功');
      setAddModalVisible(false);
      form.resetFields();
      fetchMembers(pagination.current, pagination.pageSize);
      fetchGroupInfo();
    } catch (error) {
      message.error('添加成员失败');
    }
  };

  // 删除成员
  const handleDeleteMember = async (memberId) => {
    try {
      await axios.delete(`/api/users/groups/${groupId}/members/${memberId}/`);
      message.success('删除成员成功');
      fetchMembers(pagination.current, pagination.pageSize);
      fetchGroupInfo();
    } catch (error) {
      message.error('删除成员失败');
    }
  };

  // 打开编辑模态框（如有扩展可用）
  const showEditModal = (member) => {
    setCurrentMember(member);
    editForm.setFieldsValue({});
    setEditModalVisible(true);
  };

  // 表格列定义
  const columns = [
    {
      title: '用户',
      key: 'user',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.user.username}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.user.email}</div>
        </div>
      )
    },
    {
      title: '添加时间',
      key: 'joined_at',
      render: (_, record) => new Date(record.joined_at).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const menu = (
          <Menu>
            {/* 预留编辑功能 */}
            {/* <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => showEditModal(record)}>
              编辑
            </Menu.Item> */}
            <Menu.Item key="delete" icon={<DeleteOutlined />}>
              <Popconfirm
                title="确定要删除此成员吗？"
                onConfirm={() => handleDeleteMember(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <span style={{ color: '#ff4d4f' }}>删除</span>
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      }
    }
  ];

  // 过滤已添加的用户
  const getAvailableUsers = () => {
    const memberUserIds = members.map(member => member.user.id);
    return users.filter(user => !memberUserIds.includes(user.id));
  };

  useEffect(() => {
    if (groupId) {
      fetchGroupInfo();
      fetchMembers();
      fetchUsers();
    }
  }, [groupId]);

  return (
    <div style={{ padding: '24px' }}>
      {/* 面包屑导航 */}
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/user')}
            style={{ padding: 0 }}
          >
            {groupInfo ? groupInfo.name : '用户组'}
          </Button>
        </Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <Input.Search
            placeholder="搜索用户名/邮箱"
            allowClear
            style={{ width: 220 }}
            value={searchUser}
            onChange={e => setSearchUser(e.target.value)}
            onSearch={value => fetchMembers(1, pagination.pageSize, value)}
          />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalVisible(true)}
        >
          添加成员
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={members}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          onChange: (page, pageSize) => fetchMembers(page, pageSize)
        }}
      />

      {/* 添加成员模态框 */}
      <Modal
        title="添加成员"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddMember}
        >
          <Form.Item
            name="user_id"
            label="选择用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              placeholder="请选择要添加的用户"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getAvailableUsers().map(user => (
                <Option key={user.id} value={user.id}>
                  {user.username} ({user.email})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => {
                setAddModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserGroupMemberManage; 